import { devLog } from '../../../utils/devLog';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Chip,
  IconButton,
  Typography,
  Stack,
  Button,
  CircularProgress,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  Alert,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Scanner as ScannerIcon,
  PersonAdd as PersonAddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { get, post } from '../../../api/apiClient';
import * as agentApi from '../../../api/agentApi';
import { fetchRawTenantAgents } from '../../../api/agentApi';
import { getTenantsAdminList } from '../../../api/backendApi';
import { canRequestPlatformTenantList, tenantScopedResourceId } from '../../../utils/platformTenantAccess';
import { useAgentAuthorization } from '../../../hooks/useAgentAuthorization';
import { useAuth } from '../../../contexts/AuthContext';
import { useDealerAdmin } from '../../../contexts/DealerAdminContext';
import { useTenant } from '../../../libs/contexts/TenantContext';
import {
  buildTenantScopedSegmentsQuery,
  countDuplicateSegmentDocs,
  deduplicateSegments,
} from '../../../utils/segmentDedup';
import AgentEditModal from './AgentEditModal';
import AgentCreateModal from './AgentCreateModal';
import AddEmbeddedAgentModal from './AddEmbeddedAgentModal';
import UnifiedAgentsTable from './UnifiedAgentsTable';
import { getFirestore, collection, onSnapshot } from 'firebase/firestore';
import { healthFromAgentDocument } from '../../../utils/agentHealthModel';
import {
  apiErrorMessage,
  getAgentDisplayName,
  getPrimaryDisplayName,
  getSegmentDisplayCidr,
  isDismissibleStaleAgent,
  listBulkDeletableAgents,
  listCombinedLanAndEmbeddedAgents,
  listEmbeddedEdgeAgents,
  listLanWorkstationAgents,
  nextSegmentSort,
  segmentStatusPresentation,
  liveScanTargets,
  segmentsIncludingLiveNetworks,
  sortSegments,
} from './segmentAgentsTable';

const SEGMENT_COLUMNS = [
  { key: 'cidr', label: 'Segment (CIDR)' },
  { key: 'status', label: 'Status' },
  { key: 'primary', label: 'Primary Agent' },
  { key: 'standbyCount', label: 'Standby Agents' },
  { key: 'devicesCount', label: 'Devices' },
];

const SEGMENT_TABLE_COLSPAN = SEGMENT_COLUMNS.length;

function normalizeListedAgent(agent) {
  return {
    agentId: agent.agent_id || agent.agentId,
    status: agent.status,
    quality: agent.quality,
    lastHeartbeat: agent.lastHeartbeatAt || agent.last_heartbeat || agent.lastHeartbeat || agent.lastSeen,
    lastHeartbeatAt: agent.lastHeartbeatAt || agent.last_heartbeat || agent.lastHeartbeat || agent.lastSeen,
    expectedHeartbeatSeconds: agent.expectedHeartbeatSeconds || agent.expected_heartbeat_seconds,
    diagnostics: agent.diagnostics && typeof agent.diagnostics === 'object' ? agent.diagnostics : undefined,
    mqttConnected: agent.mqttConnected,
    enabled: agent.enabled,
    disabled: agent.disabled,
    deleted: agent.deleted,
    revokedAt: agent.revokedAt,
    runtimeClass: agent.runtimeClass || agent.runtime_class,
    tenantId: agent.tenant_id || agent.tenantId,
    siteId: agent.siteId || agent.site_id,
    segment: agent.segment || agent.segmentId,
    segmentId: agent.segmentId || agent.segment_id || agent.segment,
    lanIpv4: agent.lanIpv4 || agent.ip || agent.localIp || '',
    lanInterface: agent.lanInterface || '',
    ip: agent.ip || '',
    localIp: agent.localIp || '',
    name: agent.name || agent.agentName || agent.displayName || '',
    formFactor: agent.formFactor || agent.form_factor,
    platform: agent.platform,
    deviceId:
      agent.deviceId ||
      agent.device_id ||
      agent.device_info?.device_id ||
      agent.deviceInfo?.device_id,
  };
}

async function fetchAgentsForTenant(tenantId) {
  if (!tenantId) return [];
  const agents = await fetchRawTenantAgents(tenantId);
  return agents.map(normalizeListedAgent);
}

/**
 * Segment Row Component
 */
const SegmentRow = ({ segment, onAssignAgent, onEditAgent, allAgents, editLoadingAgentId }) => {
  const displayCidr = getSegmentDisplayCidr(segment);
  const primaryName = getPrimaryDisplayName(segment, allAgents);
  const primaryAgent = (allAgents || []).find((agent) => agent.agentId === segment.primaryAgentId);
  const primaryHealth = primaryAgent ? healthFromAgentDocument(primaryAgent) : null;
  const routing = segmentStatusPresentation(segment, allAgents);
  return (
      <TableRow hover>
        <TableCell>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
            {displayCidr || segment.cidr}
          </Typography>
        </TableCell>
        <TableCell>
          <Chip
            label={routing.label}
            color={routing.color}
            size="small"
          />
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: primaryName ? 600 : 400 }}>
                {primaryName || segment.primaryAgentId || 'None'}
              </Typography>
              {primaryName && segment.primaryAgentId && (
                <Typography variant="caption" sx={{ display: 'block', fontFamily: 'monospace', color: 'text.secondary' }}>
                  {segment.primaryAgentId}
                </Typography>
              )}
              {primaryHealth && (
                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                  Agent {primaryHealth.state}{primaryHealth.reasons?.length ? ` (${primaryHealth.reasons.join(', ')})` : ''}
                </Typography>
              )}
            </Box>
            {!!segment.primaryAgentId && (
              <Tooltip title="Edit primary agent (settings / activation code)">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => onEditAgent(segment.primaryAgentId)}
                    disabled={editLoadingAgentId === segment.primaryAgentId}
                    color="secondary"
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            <Tooltip title="Assign primary agent">
              <IconButton
                size="small"
                onClick={() => onAssignAgent(segment)}
                color="primary"
              >
                <PersonAddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </TableCell>
        <TableCell>
          <Typography variant="body2">{segment.standbyCount || 0}</Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2">{segment.devicesCount || 0}</Typography>
        </TableCell>
      </TableRow>
  );
};

/**
 * Main Segments Table Component
 */
const SegmentsTable = () => {
  const { userRole, canCreateAgents, userTenant } = useAgentAuthorization();
  const { userData } = useAuth();
  const dealerAdmin = useDealerAdmin();
  const { selectedTenant } = useTenant();
  const [segments, setSegments] = useState([]);
  const [allAgents, setAllAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [agentViewMode, setAgentViewMode] = useState('lan');
  const [showAllTenants, setShowAllTenants] = useState(false);
  const [scanningAgents, setScanningAgents] = useState({});
  const [deletingAgentId, setDeletingAgentId] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, agentId: null, revoke: false });
  const [scanAllDialogOpen, setScanAllDialogOpen] = useState(false);
  const [scanningAll, setScanningAll] = useState(false);
  const [duplicateDocCount, setDuplicateDocCount] = useState(0);
  const [dedupeDialogOpen, setDedupeDialogOpen] = useState(false);
  const [dedupePreview, setDedupePreview] = useState(null);
  const [dedupeLoading, setDedupeLoading] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedSegment, setSelectedSegment] = useState(null);
  const [availableAgents, setAvailableAgents] = useState([]);
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [tenants, setTenants] = useState([]);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [editLoadingAgentId, setEditLoadingAgentId] = useState(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [embeddedModalOpen, setEmbeddedModalOpen] = useState(false);
  const [siteMap, setSiteMap] = useState({});
  const [segmentSort, setSegmentSort] = useState({ key: 'cidr', dir: 'asc' });
  const [bulkDeleteDialog, setBulkDeleteDialog] = useState({ open: false, agentIds: [], revoke: false });
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const effectiveRole = userData?.role || userRole;
  const requestedTenantId = dealerAdmin.tenantId || selectedTenant?.id || userTenant || userData?.tenantId;
  const effectiveTenantId = tenantScopedResourceId({
    role: effectiveRole,
    requestedTenantId,
    authorizedTenantId: userTenant || userData?.tenantId,
    dealerActive: dealerAdmin.active,
  });
  const effectiveTenantName = useMemo(() => {
    if (!effectiveTenantId) return '';
    const fromList = (tenants || []).find(
      (tenant) => (tenant.id || tenant.tenantId) === effectiveTenantId
    );
    const candidates = [
      fromList?.name,
      fromList?.displayName,
      dealerAdmin.tenantId === effectiveTenantId ? dealerAdmin.tenantName : '',
      selectedTenant?.id === effectiveTenantId ? selectedTenant?.name : '',
      selectedTenant?.id === effectiveTenantId ? selectedTenant?.displayName : '',
    ];
    const placeholders = new Set(['default tenant', 'assigned tenant', 'supported tenant']);
    for (const candidate of candidates) {
      const name = String(candidate || '').trim();
      if (name && !placeholders.has(name.toLowerCase())) return name;
    }
    return '';
  }, [dealerAdmin.tenantId, dealerAdmin.tenantName, effectiveTenantId, selectedTenant, tenants]);
  const isSuperadmin = effectiveRole === 'superadmin';

  const sortedSegments = useMemo(
    () => sortSegments(
      segmentsIncludingLiveNetworks(segments, allAgents),
      segmentSort.key,
      segmentSort.dir,
      allAgents,
    ),
    [allAgents, segmentSort.dir, segmentSort.key, segments]
  );

  const bulkDeletableAgents = useMemo(
    () => listBulkDeletableAgents(allAgents, segments),
    [allAgents, segments]
  );

  const lanAgents = useMemo(
    () => listLanWorkstationAgents(allAgents, segments),
    [allAgents, segments]
  );

  const embeddedEdgeAgents = useMemo(
    () => listEmbeddedEdgeAgents(allAgents, segments),
    [allAgents, segments]
  );

  const displayedAgents = useMemo(() => {
    if (!isSuperadmin || agentViewMode === 'lan') return lanAgents;
    if (agentViewMode === 'embedded') return embeddedEdgeAgents;
    return listCombinedLanAndEmbeddedAgents(lanAgents, embeddedEdgeAgents);
  }, [agentViewMode, embeddedEdgeAgents, isSuperadmin, lanAgents]);

  const visibleAgentCount = useMemo(() => {
    if (agentViewMode === 'embedded') return displayedAgents.length;
    return displayedAgents.filter((agent) => !isDismissibleStaleAgent(agent, segments)).length;
  }, [agentViewMode, displayedAgents, segments]);

  const hiddenAgentCount = displayedAgents.length - visibleAgentCount;

  // Site names are display-only labels for the Site column.
  useEffect(() => {
    if (!effectiveTenantId) return;
    
    const db = getFirestore();
    const sitesRef = collection(db, 'tenants', effectiveTenantId, 'sites');
    
    devLog('[SegmentsTable] Setting up sites subscription for tenant:', effectiveTenantId);
    
    const unsubscribe = onSnapshot(
      sitesRef,
      (snapshot) => {
        const map = {};
        snapshot.docs.forEach((doc) => {
          map[doc.id] = doc.data().name || doc.id;
        });
        devLog(`[SegmentsTable] Sites loaded: ${Object.keys(map).length} sites`, map);
        setSiteMap(map);
      },
      (error) => {
        console.error('[SegmentsTable] Error loading sites:', error?.message || 'request failed');
      }
    );
    
    return () => unsubscribe();
  }, [effectiveTenantId]);

  useEffect(() => {
    let mounted = true;
    const loadTenants = async () => {
      const canLoadTenants = canRequestPlatformTenantList(effectiveRole);
      if (!canLoadTenants) {
        if (mounted) setTenants([]);
        return;
      }
      try {
        const list = await getTenantsAdminList();
        if (!mounted) return;
        setTenants(Array.isArray(list) ? list : []);
      } catch (e) {
        if (!mounted) return;
        setTenants([]);
      }
    };
    loadTenants();
    return () => {
      mounted = false;
    };
  }, [effectiveRole]);

  /**
   * Fetch all agents for the selected tenant (paginated API).
   */
  const fetchAllAgents = useCallback(async () => {
    setAgentsLoading(true);
    try {
      if (isSuperadmin && showAllTenants) {
        const tenantList = tenants.length > 0 ? tenants : await getTenantsAdminList();
        const collected = [];
        for (const tenant of Array.isArray(tenantList) ? tenantList : []) {
          if (!tenant?.id) continue;
          const tenantAgents = await fetchAgentsForTenant(tenant.id);
          tenantAgents.forEach((agent) => {
            collected.push({
              ...agent,
              tenantName: tenant.name || tenant.displayName || tenant.id,
            });
          });
        }
        devLog(`[SegmentsTable] Loaded ${collected.length} agents across all tenants`);
        setAllAgents(collected);
        return;
      }

      if (!effectiveTenantId) {
        setAllAgents([]);
        return;
      }
      const normalizedAgents = await fetchAgentsForTenant(effectiveTenantId);
      devLog(`[SegmentsTable] Loaded ${normalizedAgents.length} agents for tenant ${effectiveTenantId}`);
      setAllAgents(normalizedAgents);
    } catch (error) {
      console.error('Error fetching agents:', error?.message || 'request failed');
      setAllAgents([]);
    } finally {
      setAgentsLoading(false);
    }
  }, [effectiveTenantId, isSuperadmin, showAllTenants, tenants]);

  const fetchSegments = useCallback(async () => {
    setLoading(true);
    try {
      const query = buildTenantScopedSegmentsQuery(effectiveTenantId, effectiveRole, dealerAdmin.active);
      const response = await get(`/api/v2/admin/segments/with-agents${query}`);
      if (response?.success) {
        const raw = response.data.segments || [];
        const deduped = deduplicateSegments(raw);
        const dupCount = response.data.meta?.duplicateDocCount ?? countDuplicateSegmentDocs(raw);
        devLog(`[SegmentsTable] Segments: ${raw.length} raw → ${deduped.length} after dedup`);
        setDuplicateDocCount(dupCount);
        setSegments(deduped);
      } else {
        toast.error('Failed to load segments');
      }
    } catch (error) {
      console.error('Error fetching segments:', error?.message || 'request failed');
      toast.error(apiErrorMessage(error, 'Failed to load segments'));
    } finally {
      setLoading(false);
    }
  }, [dealerAdmin.active, effectiveTenantId, effectiveRole]);

  const handleOpenDedupeDialog = useCallback(async () => {
    if (!effectiveTenantId) {
      toast.error('Select a tenant first');
      return;
    }
    setDedupeLoading(true);
    try {
      const query = buildTenantScopedSegmentsQuery(effectiveTenantId, effectiveRole, dealerAdmin.active);
      const response = await get(`/api/v2/admin/segments/duplicates${query}`);
      if (response?.success) {
        setDedupePreview(response.data || null);
        setDedupeDialogOpen(true);
      } else {
        toast.error('Failed to preview duplicate segments');
      }
    } catch (error) {
      console.error('Error previewing duplicate segments:', error?.message || 'request failed');
      toast.error(apiErrorMessage(error, 'Failed to preview duplicate segments'));
    } finally {
      setDedupeLoading(false);
    }
  }, [dealerAdmin.active, effectiveTenantId, effectiveRole]);

  const handleConfirmDedupe = useCallback(async () => {
    if (!effectiveTenantId) return;
    setDedupeLoading(true);
    try {
      const response = await post('/api/v2/admin/segments/deduplicate', {
        tenantId: effectiveTenantId,
        dryRun: false,
      });
      if (response?.success) {
        const deletedCount = response.data?.deleted?.length || 0;
        const blockedCount = response.data?.blocked?.length || 0;
        const rewiredCount = response.data?.rewiredCount || response.data?.rewired?.length || 0;
        toast.success(response.data?.message || `Removed ${deletedCount} duplicate segment doc(s).`);
        if (rewiredCount > 0) {
          toast.info(`Rewired ${rewiredCount} device/agent reference(s) to the canonical segment.`);
        }
        if (blockedCount > 0) {
          toast.warning(`${blockedCount} duplicate doc(s) were blocked due to exclusive references.`);
        }
        setDedupeDialogOpen(false);
        setDedupePreview(null);
        fetchSegments();
      } else {
        toast.error('Failed to remove duplicate segments');
      }
    } catch (error) {
      console.error('Error removing duplicate segments:', error?.message || 'request failed');
      toast.error(apiErrorMessage(error, 'Failed to remove duplicate segments'));
    } finally {
      setDedupeLoading(false);
    }
  }, [effectiveTenantId, fetchSegments]);

  useEffect(() => {
    fetchSegments();
    fetchAllAgents();
  }, [fetchSegments, fetchAllAgents]);

  const handleOpenEditAgent = useCallback(async (agentId) => {
    if (!agentId) return;
    setEditLoadingAgentId(agentId);
    try {
      const fullAgent = await agentApi.fetchAgentById(agentId);
      setEditingAgent(fullAgent);
      setEditModalOpen(true);
    } catch (error) {
      console.error('Error fetching agent details:', error?.message || 'request failed');
      toast.error('Failed to load agent details');
    } finally {
      setEditLoadingAgentId(null);
    }
  }, []);

  const handleCloseEditModal = useCallback(() => {
    setEditModalOpen(false);
    setEditingAgent(null);
  }, []);

  const handleSaveEditedAgent = useCallback(async (updatedAgent) => {
    try {
      await agentApi.updateAgent(updatedAgent.agentId, updatedAgent);
      toast.success(`Agent ${updatedAgent.agentId} updated`);
      handleCloseEditModal();
      fetchSegments();
      fetchAllAgents();
    } catch (error) {
      console.error('Error updating agent:', error?.message || 'request failed');
      toast.error('Failed to update agent');
    }
  }, [fetchAllAgents, fetchSegments, handleCloseEditModal]);

  const handleOpenCreateModal = useCallback(() => {
    setCreateModalOpen(true);
  }, []);

  const handleCloseCreateModal = useCallback(() => {
    setCreateModalOpen(false);
  }, []);

  const handleCreateAgent = useCallback(async (newAgent) => {
    try {
      const created = await agentApi.adminCreateAgent(newAgent);
      toast.success(`Agent created: ${created.agentId || created.agent_id || 'OK'}`);
      setCreateModalOpen(false);
      fetchSegments();
      fetchAllAgents();
    } catch (error) {
      console.error('Error creating agent:', error?.message || 'request failed');
      toast.error('Failed to create agent');
    }
  }, [fetchAllAgents, fetchSegments]);

  /**
   * Open the delete-agent confirmation dialog.
   */
  const handleDeleteAgent = useCallback((agentId) => {
    if (!agentId) return;
    setDeleteDialog({ open: true, agentId, revoke: false });
  }, []);

  const closeDeleteDialog = useCallback(() => {
    setDeleteDialog({ open: false, agentId: null, revoke: false });
  }, []);

  const confirmDeleteAgent = useCallback(async () => {
    const { agentId, revoke } = deleteDialog;
    if (!agentId) return;
    setDeletingAgentId(agentId);
    try {
      await agentApi.deleteAgent(agentId, { revoke });
      toast.success(
        revoke
          ? `Agent ${agentId} revoked — re-activation required`
          : `Agent ${agentId} deleted`
      );
      closeDeleteDialog();
      fetchSegments();
      fetchAllAgents();
    } catch (error) {
      console.error('Error deleting agent:', error?.message || 'request failed');
      const msg = error?.response?.data?.message || error?.message || 'Failed to delete agent';
      toast.error(msg);
    } finally {
      setDeletingAgentId(null);
    }
  }, [closeDeleteDialog, deleteDialog, fetchAllAgents, fetchSegments]);

  const openBulkDeleteDialog = useCallback(() => {
    const ids = bulkDeletableAgents.map((a) => a.agentId).filter(Boolean);
    if (!ids.length) return;
    setBulkDeleteDialog({ open: true, agentIds: ids, revoke: false });
  }, [bulkDeletableAgents]);

  const closeBulkDeleteDialog = useCallback(() => {
    if (bulkDeleting) return;
    setBulkDeleteDialog({ open: false, agentIds: [], revoke: false });
  }, [bulkDeleting]);

  const confirmBulkDeleteAgents = useCallback(async () => {
    const { agentIds, revoke } = bulkDeleteDialog;
    if (!agentIds?.length) return;
    setBulkDeleting(true);
    const failed = [];
    for (const agentId of agentIds) {
      try {
        await agentApi.deleteAgent(agentId, { revoke });
      } catch (error) {
        console.error('Error deleting agent:', error?.message || 'request failed');
        failed.push(agentId);
      }
    }
    setBulkDeleting(false);
    closeBulkDeleteDialog();
    if (failed.length === 0) {
      toast.success(`Deleted ${agentIds.length} stale offline agent${agentIds.length === 1 ? '' : 's'}`);
    } else {
      toast.warning(`Deleted ${agentIds.length - failed.length}; failed ${failed.length}`);
    }
    fetchSegments();
    fetchAllAgents();
  }, [bulkDeleteDialog, closeBulkDeleteDialog, fetchAllAgents, fetchSegments]);

  const handleScanAgent = useCallback(async (segment, agentId) => {
    setScanningAgents((prev) => ({ ...prev, [agentId]: true }));
    try {
      const response = await post(
        `/api/v2/admin/segments/${encodeURIComponent(segment.segmentId)}/discover`,
        {
          agentId,
        }
      );
      if (response?.success) {
        toast.success(`Scan started for ${getSegmentDisplayCidr(segment) || segment.cidr} via ${agentId}`);
      } else {
        toast.error(apiErrorMessage(response, 'Failed to start scan'));
      }
    } catch (error) {
      console.error('Error scanning segment:', error?.message || 'request failed');
      toast.error(apiErrorMessage(error, 'Failed to start scan'));
    } finally {
      setScanningAgents((prev) => {
        const newState = { ...prev };
        delete newState[agentId];
        return newState;
      });
    }
  }, []);

  /**
   * Open assign agent dialog
   */
  const handleOpenAssignDialog = useCallback(async (segment) => {
    setSelectedSegment(segment);
    setSelectedAgentId(segment.primaryAgentId || '');
    setAssignDialogOpen(true);

    try {
      if (!effectiveTenantId) {
        setAvailableAgents([]);
        return;
      }
      const normalizedAgents = await fetchAgentsForTenant(effectiveTenantId);
      setAvailableAgents(normalizedAgents);
    } catch (error) {
      console.error('Error fetching agents:', error?.message || 'request failed');
      toast.error('Failed to load agents');
    }
  }, [effectiveTenantId]);

  /**
   * Assign agent to segment
   */
  const handleAssignAgent = useCallback(async () => {
    if (!selectedSegment || !selectedAgentId) {
      toast.error('Please select an agent');
      return;
    }

    setAssigning(true);
    try {
      const response = await post(
        `/api/v2/admin/segments/${encodeURIComponent(selectedSegment.segmentId)}/assign-agent`,
        {
          primaryAgentId: selectedAgentId,
          reason: 'Manual assignment via UI'
        }
      );

      if (response?.success) {
        toast.success(`Agent ${selectedAgentId} assigned to segment ${getSegmentDisplayCidr(selectedSegment) || selectedSegment.cidr}`);
        setAssignDialogOpen(false);
        fetchSegments(); // Refresh the list
      } else {
        toast.error('Failed to assign agent');
      }
    } catch (error) {
      console.error('Error assigning agent:', error?.message || 'request failed');
      toast.error(apiErrorMessage(error, 'Failed to assign agent'));
    } finally {
      setAssigning(false);
    }
  }, [selectedSegment, selectedAgentId, fetchSegments]);

  /**
   * Scan the subnet each online agent is actually on.
   * Stored segment rows can lag a router change; the agent heartbeat is current.
   */
  const handleScanAll = useCallback(async () => {
    if (!effectiveTenantId) {
      toast.error('Select a tenant first');
      return;
    }
    const targets = liveScanTargets(allAgents);
    setScanAllDialogOpen(false);
    setScanningAll(true);

    if (!targets.length) {
      toast.warning('Scan skipped: no online agent on a subnet');
      setScanningAll(false);
      return;
    }

    let scanned = 0;
    const failures = [];
    try {
      for (const target of targets) {
        try {
          const response = await post(
            `/api/v2/admin/agents/${encodeURIComponent(target.agentId)}/scan`,
            { tenantId: effectiveTenantId, cidr: target.cidr }
          );
          if (response?.success) scanned += 1;
          else failures.push(target);
        } catch (error) {
          console.error('Error scanning subnet:', error?.message || 'request failed');
          failures.push({ ...target, reason: apiErrorMessage(error, 'Failed to start scan') });
        }
      }
      if (scanned === 0) {
        const reason = failures[0]?.reason || 'the agent did not accept the scan';
        toast.warning(`Scan skipped: ${reason} (${targets[0].cidr})`);
      } else if (failures.length) {
        toast.warning(`Scan started for ${scanned} subnet${scanned === 1 ? '' : 's'}; ${failures.length} failed`);
      } else {
        const summary = targets.map((target) => `${target.cidr} via ${target.name}`).join(', ');
        toast.success(`Scan started: ${summary}`);
      }
    } finally {
      setScanningAll(false);
    }
  }, [allAgents, effectiveTenantId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const agentsSectionTitle =
    agentViewMode === 'embedded'
      ? 'Embedded / Edge Agents'
      : agentViewMode === 'all'
        ? 'All Agents (LAN + Embedded)'
        : 'LAN / Workstation Agents';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pb: 4 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Typography variant="h5" component="h2">
          Agents & Network Segments
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {isSuperadmin && (agentViewMode === 'embedded' || agentViewMode === 'all') && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => setEmbeddedModalOpen(true)}
            >
              Add Embedded Agent
            </Button>
          )}
          {typeof canCreateAgents === 'function' && canCreateAgents() && (
            <Button variant="contained" onClick={handleOpenCreateModal}>
              Add New Agent
            </Button>
          )}
          {isSuperadmin && duplicateDocCount > 0 && !showAllTenants && (
            <Button
              variant="outlined"
              color="warning"
              startIcon={dedupeLoading ? <CircularProgress size={16} /> : <DeleteIcon />}
              onClick={handleOpenDedupeDialog}
              disabled={loading || dedupeLoading}
            >
              Remove {duplicateDocCount} Duplicate Segment{duplicateDocCount === 1 ? '' : 's'}
            </Button>
          )}
          {bulkDeletableAgents.length > 0 && !showAllTenants && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={openBulkDeleteDialog}
              disabled={loading || bulkDeleting}
            >
              Remove {bulkDeletableAgents.length} Stale Offline Agent{bulkDeletableAgents.length === 1 ? '' : 's'}
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={scanningAll ? <CircularProgress size={16} /> : <ScannerIcon />}
            onClick={() => setScanAllDialogOpen(true)}
            disabled={!effectiveTenantId || scanningAll || loading || showAllTenants}
          >
            {scanningAll ? 'Scanning...' : 'Scan Network'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => {
              fetchSegments();
              fetchAllAgents();
            }}
            disabled={loading || agentsLoading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 0 }}>
        <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle1" component="h3">
            Network Segments
          </Typography>
          {showAllTenants && (
            <Typography variant="body2" color="text.secondary">
              Segments are shown for the selected tenant only.
            </Typography>
          )}
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {SEGMENT_COLUMNS.map((col) => (
                  <TableCell
                    key={col.key}
                    sortDirection={segmentSort.key === col.key ? segmentSort.dir : false}
                    sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}
                  >
                    <TableSortLabel
                      active={segmentSort.key === col.key}
                      direction={segmentSort.key === col.key ? segmentSort.dir : 'asc'}
                      onClick={() => setSegmentSort((prev) => nextSegmentSort(prev, col.key))}
                    >
                      {col.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedSegments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={SEGMENT_TABLE_COLSPAN} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                      No segments found. Segments are created automatically when agents report their
                      network configuration.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sortedSegments.map((segment) => (
                  <SegmentRow
                    key={segment.segmentId}
                    segment={segment}
                    onAssignAgent={handleOpenAssignDialog}
                    onEditAgent={handleOpenEditAgent}
                    allAgents={allAgents}
                    editLoadingAgentId={editLoadingAgentId}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant="subtitle1" component="h3">
              {agentsSectionTitle}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {visibleAgentCount} active agent{visibleAgentCount === 1 ? '' : 's'}
              {showAllTenants ? ' across all tenants' : ''}
              {hiddenAgentCount > 0 && agentViewMode !== 'embedded'
                ? ` · ${hiddenAgentCount} inactive hidden`
                : ''}
            </Typography>
          </Box>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
            {isSuperadmin && (
              <ToggleButtonGroup
                exclusive
                size="small"
                value={agentViewMode}
                onChange={(_event, value) => {
                  if (value) setAgentViewMode(value);
                }}
              >
                <ToggleButton value="lan">LAN / Real</ToggleButton>
                <ToggleButton value="embedded">Embedded</ToggleButton>
                <ToggleButton value="all">All</ToggleButton>
              </ToggleButtonGroup>
            )}
            {isSuperadmin && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showAllTenants}
                    onChange={(e) => setShowAllTenants(e.target.checked)}
                  />
                }
                label="All tenants"
              />
            )}
          </Stack>
        </Stack>

        {agentsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <UnifiedAgentsTable
            key={agentViewMode}
            agents={displayedAgents}
            viewMode={isSuperadmin ? agentViewMode : 'lan'}
            segments={segments}
            siteMap={siteMap}
            showTenantColumn={showAllTenants}
            showStaleToggle={agentViewMode !== 'embedded'}
            onEditAgent={handleOpenEditAgent}
            onDeleteAgent={handleDeleteAgent}
            onScanAgent={handleScanAgent}
            editLoadingAgentId={editLoadingAgentId}
            deletingAgentId={deletingAgentId}
            scanningAgents={scanningAgents}
          />
        )}
      </Paper>

      {/* Assign Agent Dialog */}
      <Dialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign Primary Agent</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Select an agent to assign as the primary agent for segment{' '}
            <strong>{getSegmentDisplayCidr(selectedSegment) || selectedSegment?.cidr}</strong>
          </DialogContentText>
          <FormControl fullWidth>
            <InputLabel id="agent-select-label">Agent</InputLabel>
            <Select
              labelId="agent-select-label"
              value={selectedAgentId}
              label="Agent"
              onChange={(e) => setSelectedAgentId(e.target.value)}
            >
              {availableAgents.map((agent) => (
                <MenuItem key={agent.agentId} value={agent.agentId}>
                  {getAgentDisplayName(agent) || agent.agentId} — {agent.agentId} — {agent.status}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialogOpen(false)} disabled={assigning}>
            Cancel
          </Button>
          <Button
            onClick={handleAssignAgent}
            variant="contained"
            disabled={!selectedAgentId || assigning}
            startIcon={assigning ? <CircularProgress size={20} /> : null}
          >
            Assign
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dedupeDialogOpen} onClose={() => !dedupeLoading && setDedupeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Remove duplicate segment documents?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            This keeps one canonical segment document per subnet (CIDR) and deletes legacy duplicate
            Firestore docs for tenant <strong>{effectiveTenantId}</strong>.
          </DialogContentText>
          {dedupePreview && (
            <Alert severity="info" sx={{ mb: 1 }}>
              {dedupePreview.duplicateDocCount || 0} duplicate doc(s) across {dedupePreview.duplicateGroups || 0} subnet(s).
              {(dedupePreview.rewiredCount || dedupePreview.rewired?.length || 0) > 0 && (
                <> {(dedupePreview.rewiredCount || dedupePreview.rewired.length)} device/agent reference(s) will be rewired to the canonical segment.</>
              )}
              {(dedupePreview.blocked?.length || 0) > 0 && (
                <> {dedupePreview.blocked.length} would remain blocked after rewire.</>
              )}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDedupeDialogOpen(false)} disabled={dedupeLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDedupe}
            variant="contained"
            color="warning"
            disabled={dedupeLoading}
            startIcon={dedupeLoading ? <CircularProgress size={16} /> : null}
          >
            Remove Duplicates
          </Button>
        </DialogActions>
      </Dialog>

      {/* Scan All Confirmation Dialog */}
      <Dialog open={scanAllDialogOpen} onClose={() => !scanningAll && setScanAllDialogOpen(false)}>
        <DialogTitle>Scan all network segments?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This scans the subnet each online agent is on
            {effectiveTenantName ? <> for <strong>{effectiveTenantName}</strong></> : null}
            {' '}and may generate network traffic. Offline agents are left alone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScanAllDialogOpen(false)} disabled={scanningAll}>Cancel</Button>
          <Button
            onClick={handleScanAll}
            variant="contained"
            autoFocus
            disabled={scanningAll || !effectiveTenantId}
            startIcon={scanningAll ? <CircularProgress size={16} /> : <ScannerIcon />}
          >
            Scan All
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={bulkDeleteDialog.open}
        onClose={closeBulkDeleteDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Remove stale offline agents?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            This will delete {bulkDeleteDialog.agentIds.length} offline agent
            {bulkDeleteDialog.agentIds.length === 1 ? '' : 's'} that are not primary for any segment
            in the current tenant.
          </DialogContentText>
          <Box component="ul" sx={{ pl: 2, m: 0, maxHeight: 200, overflow: 'auto' }}>
            {bulkDeleteDialog.agentIds.map((id) => {
              const agent = allAgents.find((a) => a.agentId === id);
              return (
                <li key={id}>
                  <Typography variant="body2" component="span" sx={{ fontFamily: 'monospace' }}>
                    {getAgentDisplayName(agent) || id} ({id})
                  </Typography>
                </li>
              );
            })}
          </Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={bulkDeleteDialog.revoke}
                onChange={(e) =>
                  setBulkDeleteDialog((prev) => ({ ...prev, revoke: e.target.checked }))
                }
              />
            }
            label="Also revoke credentials (require re-activation)"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeBulkDeleteDialog} disabled={bulkDeleting}>Cancel</Button>
          <Button
            onClick={confirmBulkDeleteAgents}
            color="error"
            variant="contained"
            disabled={bulkDeleting}
            startIcon={bulkDeleting ? <CircularProgress size={16} color="inherit" /> : null}
          >
            Delete {bulkDeleteDialog.agentIds.length} Agent{bulkDeleteDialog.agentIds.length === 1 ? '' : 's'}
          </Button>
        </DialogActions>
      </Dialog>

      {editModalOpen && editingAgent && (
        <AgentEditModal
          agent={editingAgent}
          onSave={handleSaveEditedAgent}
          onClose={handleCloseEditModal}
          tenants={tenants}
        />
      )}

      {createModalOpen && (
        <AgentCreateModal onSave={handleCreateAgent} onClose={handleCloseCreateModal} tenants={tenants} />
      )}

      <AddEmbeddedAgentModal
        open={embeddedModalOpen}
        onClose={() => setEmbeddedModalOpen(false)}
        onSuccess={() => {
          fetchSegments();
          fetchAllAgents();
        }}
      />

      {/* Delete Agent Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={deletingAgentId ? undefined : closeDeleteDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete agent?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Agent <strong style={{ fontFamily: 'monospace' }}>{deleteDialog.agentId}</strong> will be
            removed from this tenant. Stale references in segments and devices will be cleaned up
            automatically.
          </DialogContentText>
          <FormControlLabel
            control={
              <Checkbox
                checked={deleteDialog.revoke}
                onChange={(e) =>
                  setDeleteDialog((prev) => ({ ...prev, revoke: e.target.checked }))
                }
              />
            }
            label="Also revoke credentials (require re-activation)"
          />
          {deleteDialog.revoke ? (
            <Alert severity="warning" sx={{ mt: 2 }}>
              The agent's current activation will be invalidated. It cannot reconnect until an admin
              issues a new activation code and it is re-activated on the device.
            </Alert>
          ) : (
            <Alert severity="info" sx={{ mt: 2 }}>
              Soft delete: if the agent is still running it will re-register automatically on its
              next heartbeat (within ~30s) using its existing credentials.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} disabled={!!deletingAgentId}>
            Cancel
          </Button>
          <Button
            onClick={confirmDeleteAgent}
            color="error"
            variant="contained"
            disabled={!!deletingAgentId}
            startIcon={deletingAgentId ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {deleteDialog.revoke ? 'Revoke & Delete' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SegmentsTable;
